import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getAdminUsers, updateAdminUser, deleteAdminUser, AdminUser } from '../../api/admin'
import { useAuth } from '../../AuthContext'

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: getAdminUsers,
  })

  const mutation = useMutation({
    mutationFn: (payload: { id: number; role?: string; is_active?: number }) =>
      updateAdminUser(payload.id, {
        role: payload.role,
        is_active: payload.is_active,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteAdminUser(id),
    onSuccess: () => {
      setDeleteTarget(null)
      setDeleteConfirmEmail('')
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  const users: AdminUser[] = useMemo(() => data?.users ?? [], [data])

  if (!currentUser || currentUser.role !== 'system_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded shadow-md p-6 max-w-md w-full text-center">
          <h1 className="text-xl font-semibold mb-2">Доступ запрещён</h1>
          <p className="text-sm text-gray-600">
            Для просмотра страницы управления пользователями необходимы права системного администратора.
          </p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100"><div><p className="text-sm text-gray-600">Загрузка пользователей...</p></div></div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded shadow-md p-6 max-w-md w-full text-center">
          <p className="text-sm text-red-700">Ошибка загрузки пользователей.</p>
        </div>
      </div>
    )
  }

  const handleToggleActive = (u: AdminUser) => {
    mutation.mutate({
      id: u.id,
      is_active: u.is_active === 1 ? 0 : 1,
    })
  }

  const handleRoleChange = (u: AdminUser, role: string) => {
    mutation.mutate({
      id: u.id,
      role,
    })
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    if (deleteConfirmEmail.trim().toLowerCase() !== deleteTarget.email.toLowerCase()) return
    deleteMutation.mutate(deleteTarget.id)
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-blue-600">
        <div className="container mx-auto px-4 py-2 flex justify-between items-center">
          <div className="text-white text-xl font-bold">Админ – Пользователи</div>
          <div className="text-xs text-blue-100">
            Вход выполнен как <span className="font-semibold">{currentUser.email}</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="bg-white rounded shadow-md p-4">
          <h2 className="text-lg font-semibold mb-4">Пользователи</h2>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium text-gray-700">ID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Имя / Email</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Роль</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Активен</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Email подтверждён</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Создан</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === currentUser.id
                  return (
                    <tr key={u.id} className="border-b last:border-b-0">
                      <td className="px-3 py-2 text-gray-800">{u.id}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-left hover:underline"
                          onClick={() => navigate(`/users/${u.id}`)}
                        >
                          <div className="font-medium text-gray-900">{u.name || '—'}</div>
                          <div className="text-gray-600 text-xs">{u.email}</div>
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          className="border rounded px-2 py-1 text-xs"
                          value={u.role}
                          onChange={(e) => handleRoleChange(u, e.target.value)}
                          disabled={mutation.isPending || isSelf}
                        >
                          <option value="user">Пользователь</option>
                          <option value="system_admin">Системный администратор</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(u)}
                          disabled={mutation.isPending || isSelf}
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            u.is_active === 1
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {u.is_active === 1 ? 'Активен' : 'Неактивен'}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-gray-800">
                        {u.email_verified_at ? 'Да' : 'Нет'}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {u.created_at ? u.created_at : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="px-2 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-800 hover:bg-slate-200"
                            onClick={() => navigate(`/users/${u.id}`)}
                          >
                            Профиль
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800 hover:bg-blue-200"
                            onClick={() => navigate(`/admin/users/${u.id}/access`)}
                          >
                            Доступ…
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800 hover:bg-red-200 disabled:opacity-40"
                            onClick={() => {
                              setDeleteTarget(u)
                              setDeleteConfirmEmail('')
                            }}
                            disabled={isSelf || deleteMutation.isPending}
                          >
                            Удалить
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {mutation.isPending && (
            <div className="mt-3 text-xs text-gray-500">Сохранение изменений...</div>
          )}
        </div>
      </main>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Удалить пользователя?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Будут удалены сессии, права доступа и связанные данные AI. Это необратимо.
              <br />
              <span className="font-medium">{deleteTarget.name}</span> ({deleteTarget.email})
            </p>
            <label className="block text-xs text-gray-600 mb-1">
              Введите email для подтверждения: <span className="font-mono">{deleteTarget.email}</span>
            </label>
            <input
              type="email"
              value={deleteConfirmEmail}
              onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-4"
              autoComplete="off"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50"
                onClick={() => {
                  setDeleteTarget(null)
                  setDeleteConfirmEmail('')
                }}
              >
                Отмена
              </button>
              <button
                type="button"
                className="px-3 py-2 text-sm rounded bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50"
                disabled={
                  deleteMutation.isPending ||
                  deleteConfirmEmail.trim().toLowerCase() !== deleteTarget.email.toLowerCase()
                }
                onClick={handleDelete}
              >
                {deleteMutation.isPending ? 'Удаление…' : 'Удалить навсегда'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


export default AdminUsersPage
